<template>
    <div class="draggable" :class="getBGClass()" v-if="(task.status!=='OMITTED') || showOmitted ">
        <!-- Task Header. -->
        <div class="flex items-center mb-1">
            <div class="w-6 flex-shrink-0">
                <i class="fa fa-bars text-sm text-gray-400 hover:text-gray-500 cursor-move handle" v-if="canMove()"></i>
            </div>
            <div class="w-16">
                <div v-if="!canEdit()"
                     class="whitespace-pre-line text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                     :class="((hasChanged('reference') || (status==='OMITTED') || (proposedStatus==='OMITTED')) ?' text-red-600':'') + (((status==='OMITTED') || proposedStatus==='OMITTED')?' line-through':'') + getBGClass()"
                >
                    <div class="line-through font-medium text-gray-400" v-if="operationsFromExists('reference')"
                         v-text="operationsFrom('reference')"></div>
                    <div :class="operationsFromExists('reference')?'font-semibold':''" v-text="reference"></div>
                </div>
                <input
                    :placeholder="'Ref...'"
                    class="text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                    :class="((hasChanged('reference') || (status==='OMITTED') || (proposedStatus==='OMITTED')) ?' text-red-600':'') + (((status==='OMITTED') || proposedStatus==='OMITTED')?' line-through':'') + getBGClass()"
                    ref="reference"
                    type="text"
                    @change="addSnapshot"
                    @input="update()"
                    v-else
                    v-model="reference"
                >
            </div>
            <div class="flex-1 ml-0">
                <div v-if="!canEdit()"
                     class="whitespace-pre-line text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                     :class="((hasChanged('name') || (status==='OMITTED') || (proposedStatus==='OMITTED')) ?' text-red-600':'') + (((status==='OMITTED') || proposedStatus==='OMITTED')?' line-through':'') + getBGClass()"
                >
                    <div class="line-through font-medium text-gray-400" v-if="operationsFromExists('name')"
                         v-text="operationsFrom('name')"></div>
                    <div :class="operationsFromExists('name')?'font-semibold':''" v-text="name"></div>
                </div>
                <input
                    placeholder="Untitled task..."
                    class="text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                    :class="((hasChanged('name') || (status==='OMITTED') || (proposedStatus==='OMITTED')) ?' text-red-600':'') + (((status==='OMITTED') || proposedStatus==='OMITTED')?' line-through':'') + getBGClass()"
                    v-else
                    @input="update()"
                    @change="addSnapshot"
                    type="text"
                    ref="name"
                    v-model="name"
                >
            </div>
            <div class="w-6 flex-shrink-0 ml-2 self-start py-1.5">
                <ui-dropdown menu-class="p-3 w-48"
                             v-if="(!this.readonly) && (section.status!=='OMITTED') && (section.proposedStatus!=='OMITTED')">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h fa-fw"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-trash" v-if="proposedStatus==='ADDED'" danger
                                          @click="omitTask(true); update(); close()">
                            Remove
                            addition
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-trash" v-else-if="proposedStatus!=='OMITTED'" danger
                                          @click="omitTask(true); update(); close()">Omit task
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-trash" v-else @click="omitTask(false); update(); close()">
                            Revert
                            omission
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="hasChanged()"></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-undo" v-if="hasChanged()"
                                          @click="revert(); update(); close()">
                            Revert
                            changes
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </div>
        </div>

        <!-- Task Items. -->
        <draggable :list="task.items" draggable=".draggable" handle=".handle"
                   :group="'tasks.items'+(((proposedStatus==='OMITTED') || (status==='OMITTED'))?'Off':'')"
                   @change="contractedWorks.addSnapshot(); update()">
            <contracted-works-item v-for="item in task.items" :showItemProgress="showItemProgress" :key="item.id"
                                   :search="search"
                                   :contracted-works="contractedWorks"
                                   :operations="operations"
                                   :readonly="readonly"
                                   :show-omitted="showOmitted"
                                   :section="section"
                                   :task="task"
                                   :latest="latest"
                                   @updated="update"
                                   :currently-building="currentlyBuilding"
                                   :current-viewing="currentViewing"
                                   :latest-progress-report="latestProgressReport"
                                   @editBuildup="(i) => $emit('editBuildup',i)"
                                   :item="item" :ref="(readonly?'readonly-':'')+'items'"></contracted-works-item>
        </draggable>

        <!-- New Task Item. -->
        <div class="flex border-t border-b -mb-px border-gray-200 "
             v-if="canEdit() && (section.status!=='OMITTED') && (section.proposedStatus!=='OMITTED')"
             :class="getBGClass()">
            <div class="w-6 flex-shrink-0 self-center">
                <i class="fa fa-plus text-xs text-gray-300"></i>
            </div>
            <div class="w-16">
                <input
                    :placeholder="'Ref...'"
                    class="text-sm -my-px px-2 py-1.5 border border-gray-200 shadow-none relative focus:z-10"
                    :class="getBGClass()"
                    type="text"
                    @input="addNewFromReference"
                    v-model="newItem.reference"
                >
            </div>
            <div class="flex-1">
                <input
                    placeholder="Add a new item..."
                    class="text-sm -ml-px -my-px px-1 py-1.5 border border-gray-200 shadow-none relative focus:z-10"
                    :class="getBGClass()"
                    type="text"
                    @input="addNewFromDescription"
                    v-model="newItem.description"
                >
            </div>
            <div class="w-6 flex-shrink-0 ml-2 self-start py-1.5">
            </div>
        </div>

        <!-- Total Amount. -->
        <div class="flex text-xs text-gray-500 text-right font-semibold bg-white" v-if="showTotal" :key="totalValueKey">
            <div class="flex-1 px-2 py-1">Task total</div>
            <div class="w-20 -ml-px px-2 py-1" v-text="totalValue()">
            </div>
            <div class="w-6 flex-shrink-0 ml-2"></div>
        </div>
        <div class="py-2" v-else></div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import ContractedWorksItem from "./ContractedWorksItem.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import {toCurrency, uuid} from "../../../utils";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";

export default {
    components: {
        UiDropdownDivider,
        UiDropdown,
        UiDropdownItem,
        ContractedWorksItem,
        draggable,
    },
    props: {
        search: '',
        contractedWorks: null,
        operations: [],
        latest: Object,
        section: Object,
        task: Object,
        showItemProgress: Boolean,
        showOmitted: Boolean,
        readonly: true,
        showTotal: false,
        currentlyBuilding: false,
        currentViewing: false,
        latestProgressReport: Object,
    },
    data() {
        let oSection = null;
        if (this.latest && this.section.id === 'Preliminaries') {
            oSection = this.latest.preliminaries
        } else if (this.latest) {
            oSection = this.latest.scopeOfWorks.find((i) => i.id === this.section.id);
            oSection = oSection ? oSection.tasks : null;
        }
        return {
            newItem: {
                reference: null,
                description: null,
            },
            reference: this.task.reference,
            name: this.task.name,
            proposedStatus: this.task.proposedStatus,
            status: this.task.status,
            original: oSection ? oSection.find((i) => i.id === this.task.id) : null,
            totalValueKey: 0,
        }
    },
    methods: {
        operationsFromExists(type) {
            if (this.operations) {
                let op = this.operations.find((i) => (i.subject.id === this.task.id) && (i.subject.type === 'Task') && (i.type === 'Substitution'));
                if (op) {
                    let prop = op.value[type]
                    return (typeof prop !== 'undefined' && prop !== null)
                }
            }
            return false;
        },
        operationsFrom(type) {
            if (this.operations) {
                let op = this.operations.find((i) => (i.subject.id === this.task.id) && (i.subject.type === 'Task') && (i.type === 'Substitution'));
                return op.value[type].from ? op.value[type].from : '<blank>'
            }
            return '<blank>';
        },
        totalValue() {
            let value = 0;
            this.task.items.forEach((i) => {
                if ((i.status === 'OMITTED') || (i.proposedStatus === 'OMITTED')) {
                } else {
                    value += parseFloat(i.value ? (i.value.replace(/[^\d.-]/g, '')) : 0);
                }
            })
            return toCurrency(value)
        },
        canEdit() {
            return (!this.readonly) && (this.proposedStatus !== 'OMITTED') && (this.status !== 'OMITTED') && (this.section.status !== 'OMITTED') && (this.section.proposedStatus !== 'OMITTED')
        },
        canMove() {
            return (!this.readonly) && (this.proposedStatus === 'ADDED');
        },
        omitTask(omitted) {
            if (this.proposedStatus === 'ADDED') {
                this.section.tasks.splice(this.section.tasks.indexOf(this.task), 1)
            } else {
                this.revert()
                if (omitted) {
                    this.proposedStatus = 'OMITTED'
                } else {
                    this.proposedStatus = null;
                }
                if (this.$refs.items) {
                    this.$refs.items.forEach((i) => {
                        i.omitItem(omitted)
                    })
                }
            }
            this.updateState();
        },
        addNewFromReference() {
            this.task.items.push({
                id: uuid(),
                reference: this.newItem.reference,
                description: this.newItem.description,
                proposedStatus: 'ADDED'
            })
            this.$nextTick(() => {
                this.$refs.items.at(-1).update()
                this.newItem = {reference: null, description: null}
                this.$refs.items.at(-1).$refs.reference.disableSelectAll()
                this.$refs.items.at(-1).$refs.reference.$el.focus()
                this.$refs.items.at(-1).$refs.reference.enableSelectAll()
            })
        },
        addNewFromDescription() {
            this.task.items.push({
                id: uuid(),
                reference: this.newItem.reference,
                description: this.newItem.description,
                proposedStatus: 'ADDED',
                buildup: [],
                materials: [],
                labour: [],
                fixedCosts: [],
            })
            this.$nextTick(() => {
                this.$refs.items.at(-1).update()
                this.newItem = {reference: null, description: null}
                this.$refs.items.at(-1).$refs.description.focus()
            })
        },
        updateState() {
            this.task.name = this.name
            this.task.reference = this.reference
            this.task.proposedStatus = this.proposedStatus
            this.totalValueKey++
        },
        update(snapshot) {
            this.updateState()
            this.$emit('updated', snapshot)
        },
        addSnapshot() {
            this.updateState()
            this.$emit('updated', true)
        },
        revert() {
            if (this.hasChanged()) {
                this.task.name = this.original.name
                this.name = this.task.name
                this.task.reference = this.original.reference
                this.reference = this.task.reference
            }
            this.proposedStatus = null
        },
        hasChanged(field = false) {
            if (!field) {
                return (this.original && (
                    (this.reference !== this.original.reference)
                    ||
                    (this.name !== this.original.name)
                ))
            }
            return (this.original && (this[field] !== this.original[field]))
        },
        getBGClass() {
            if (this.proposedStatus === 'OMITTED') {
                return ' bg-red-50';
            }
            if (this.proposedStatus === 'ADDED') {
                return ' bg-green-50';
            }
            if (this.hasChanged() || (this.proposedStatus === 'SUBSTITUTED')) {
                return ' bg-orange-50'
            }
            return ' bg-white';
        }
    },
}
</script>
